import { useRef, useCallback, useState } from "react";
import { useChartsStore } from "../../../store/chartStore";
import { isFeatureEnabled } from "../../../data/Services/settings";

export const useChartInteractive = () => {
  const indicatorMenuRef = useRef(null);
  const interactiveMenuRef = useRef(null);
  // Unified chart context menu (feature flag chartContextMenu)
  const chartMenuRef = useRef(null);

  // Each InteractiveLayer will create its own hook instance with the appropriate chartSectionId
  // No need for a shared interactiveApi - each chart section gets independent interactive state
  // However, we need to expose the main chart's interactive API to the parent for toolbar integration
  const [mainInteractiveApi, setMainInteractiveApi] = useState(null);

  // Callback to receive the main chart's interactive API
  const handleMainInteractiveApiReady = useCallback((api) => {
    setMainInteractiveApi(api);
  }, []);

  // Notify parent when main chart's interactive API is ready
  const onInteractiveApiReady = useCallback((api) => {
    if (typeof window.onInteractiveApiReady === "function") {
      window.onInteractiveApiReady(api);
    }
  }, []);

  // Handle right-click events on interactive drawing objects
  const handleInteractiveContextMenu = useCallback((item, moreProps, event) => {
    // Feature chartContextMenu (read at USE time): route to the unified menu,
    // which shows the "Gráfico" section plus the drawing section. Flag off →
    // legacy standalone drawing menu, untouched.
    if (isFeatureEnabled("chartContextMenu") && chartMenuRef.current) {
      chartMenuRef.current.showWithInteractive(item, moreProps, event);
      return;
    }
    // Show the context menu for interactive objects
    if (interactiveMenuRef.current) {
      interactiveMenuRef.current.show(item, moreProps, event);
    }
  }, []);

  // Handle opening edit modal directly (for auto-open on InteractiveText creation)
  const handleOpenEditModal = useCallback((interactiveId) => {
    if (interactiveMenuRef.current) {
      interactiveMenuRef.current.openEditModal(interactiveId);
    }
  }, []);

  // Store actions
  const updateInteractiveObjectAppearance = useChartsStore(
    (state) => state.updateInteractiveObjectAppearance,
  );
  const deleteInteractiveObject = useChartsStore(
    (state) => state.deleteInteractiveObject,
  );

  // Handle editing interactive objects (called from context menu)
  const handleInteractiveEdit = useCallback(
    (interactiveId, updatedAppearance) => {
      console.log("ChartWrapper.handleInteractiveEdit called:", {
        interactiveId,
        updatedAppearance,
      });

      if (!updateInteractiveObjectAppearance || !interactiveId) {
        console.log("ChartWrapper.handleInteractiveEdit early return:", {
          hasUpdateFunction: !!updateInteractiveObjectAppearance,
          hasInteractiveId: !!interactiveId,
        });
        return;
      }

      // Parse the interactive ID to extract chart info
      // Format: `${chartId}_${chartSectionId}_${objectType}_${objectIndex}`
      const parts = interactiveId.split("_");
      if (parts.length < 4) {
        console.log("ChartWrapper.handleInteractiveEdit invalid ID format:", {
          interactiveId,
          parts,
        });
        return;
      }

      const chartIdPart = parts[0];
      const chartSectionId = parts[1] === "null" ? null : parts[1];
      const objectType = parts[2];
      const objectIndex = parseInt(parts[3], 10);

      console.log("ChartWrapper.handleInteractiveEdit parsed data:", {
        chartIdPart,
        chartSectionId,
        objectType,
        objectIndex,
        updatedAppearance,
      });

      updateInteractiveObjectAppearance(
        chartIdPart,
        chartSectionId,
        objectType,
        objectIndex,
        updatedAppearance,
      );
    },
    [updateInteractiveObjectAppearance],
  );

  // Handle removing interactive objects (called from context menu)
  const handleInteractiveRemove = useCallback(
    (chartId, interactive) => {
      if (!deleteInteractiveObject || !interactive) return;

      // Extract information from the interactive object
      const chartSectionId = interactive.moreProps?.chartConfig?.id;
      const objectType = interactive.type;
      const targetObject = interactive.object;

      console.log("Removing interactive object:", {
        chartId,
        chartSectionId,
        objectType,
        targetObject,
      });

      // Get current objects to find the exact index
      const getInteractiveObjects =
        useChartsStore.getState().getInteractiveObjects;
      const currentObjects =
        getInteractiveObjects(chartId, chartSectionId) || {};
      const objectsArray = currentObjects[objectType] || [];

      // Find the index of the object to remove by comparing properties
      let objectIndex = -1;
      if (targetObject) {
        objectIndex = objectsArray.findIndex((obj) => {
          // For trend lines, compare start and end points
          if (
            objectType === "trendLine" &&
            obj.start &&
            obj.end &&
            targetObject.start &&
            targetObject.end
          ) {
            return (
              JSON.stringify(obj.start) ===
                JSON.stringify(targetObject.start) &&
              JSON.stringify(obj.end) === JSON.stringify(targetObject.end)
            );
          }
          // For other types, compare all properties
          return JSON.stringify(obj) === JSON.stringify(targetObject);
        });
      }

      if (objectIndex >= 0) {
        deleteInteractiveObject(
          chartId,
          chartSectionId,
          objectType,
          objectIndex,
        );
        console.log(`Deleted ${objectType} at index ${objectIndex}`);
      } else {
        console.warn("Could not find object to delete:", targetObject);
      }
    },
    [deleteInteractiveObject],
  );

  return {
    indicatorMenuRef,
    interactiveMenuRef,
    chartMenuRef,
    mainInteractiveApi,
    handleMainInteractiveApiReady,
    onInteractiveApiReady,
    handleInteractiveContextMenu,
    handleOpenEditModal,
    handleInteractiveEdit,
    handleInteractiveRemove,
  };
};
